import React, { useEffect, useRef, useState } from 'react'
import InstagramEmbed from './components/embeds/instagramEmbed.js'
import FacebookEmbed from './components/embeds/facebookEmbed.js'
import PinterestEmbed from './components/embeds/pinterestEmbed.js'
import TikTokEmbed from './components/embeds/tikTokEmbed.js'
import LinkedInEmbed from './components/embeds/linkedInEmbed.js'
import YouTubeEmbed from './components/embeds/youTubeEmbed.js'
import { Box, Container } from 'chemistry'
import IframeEmbed from './components/embeds/ifameEmbed.js'

const TYPES = {
  iframe: 'iframe',
  tiktok: 'tiktok',
  facebook: 'facebook',
  linkedin: 'linkedin',
  pinterest: 'pinterest',
  youtube: 'youtube',
  instagram: 'instagram'
}

const embedJsScriptSrc = '//www.instagram.com/embed.js'
const instEmbedScript = 'inst-embed-script'
const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
const isHtml = RegExp.prototype.test.bind(/(<([^>]+)>)/i)
const widthPattern = / width\s*=\s*["'](\d+)["']/i
const maxWidthPattern = / max-width:\s*(\d+)px/
const heightPattern = / height\s*=\s*["'](\d+)["']/i

const renderSwitch = (embed, hasInstagramEmbed) => {
  switch (true) {
    case embed.type.includes(TYPES.instagram):
      return <InstagramEmbed url={embed.url} height={embed.height} width={embed.width} />
    case embed.type.includes(TYPES.tiktok):
      return <TikTokEmbed url={embed.url} width={embed.width} />
    case embed.type.includes(TYPES.facebook):
      return (
        <FacebookEmbed
          url={embed.url}
          height={embed.height}
          width={embed.width}
          hasInstagramEmbed={hasInstagramEmbed}
        />
      )
    case embed.type.includes(TYPES.linkedin):
      return <LinkedInEmbed url={embed.url} height={embed.height} width={embed.width} />
    case embed.type.includes(TYPES.pinterest):
      return <PinterestEmbed url={embed.url} height={embed.height} width={embed.width} />
    case embed.type.includes(TYPES.youtube):
      return <YouTubeEmbed url={embed.url} height={embed.height} width={embed.width} />
    case embed.type.includes(TYPES.iframe):
      return <IframeEmbed dataHtml={embed.url} width={embed.width} height={embed.height} />
    default:
      break
  }
}

function getList(embedList) {
  return embedList?.map((embed, i) => {
    const url = embed.data.uri.replace('embed:', '')
    let type = ''
    let height, width

    if (urlRegex.test(url)) {
      const link = new URL(url)
      const urlParams = new URLSearchParams(link.search)

      type = link?.hostname
      height = urlParams.get('height') || urlParams.get('h') || undefined
      width = urlParams.get('width') || urlParams.get('w') || 540

      if (width > 540) {
        width = 540
      }
    } else if (isHtml(url)) {
      type = TYPES.iframe
      const widthMatch = url.match(widthPattern)
      const heightMatch = url.match(heightPattern)

      if (widthMatch) {
        width = +widthMatch[1]
      } else {
        const maxWidthMatch = url.match(maxWidthPattern)
        width = maxWidthMatch ? +maxWidthMatch[1] : undefined
      }
      if (heightMatch) {
        height = +heightMatch[1]
      }
    }

    return { url, type, height, width, i }
  })
}

export default function ({ embedList, color, isTopSpace }) {
  const [smEmbedList, setSmEmbedList] = useState(getList(embedList))
  const containerRef = useRef(null)
  const hasInstagramEmbed = smEmbedList.some(sm => sm?.type?.includes(TYPES.instagram))

  useEffect(() => {
    const updateEmbedHeight = () => {
      const containerWidth = containerRef.current.offsetWidth
      let isUpdated = false

      const updatedEmbeds = smEmbedList.map(embed => {
        if (containerWidth < embed.width && !!embed.height) {
          const aspectRatio = embed.height / embed.width
          const height = aspectRatio * containerWidth
          isUpdated = true
          return { ...embed, height: height || undefined }
        }
        return embed
      })

      if (isUpdated) {
        setSmEmbedList(updatedEmbeds)
      }
    }

    updateEmbedHeight()
    window.addEventListener('resize', updateEmbedHeight)

    return () => {
      window.removeEventListener('resize', updateEmbedHeight)
    }
  }, [smEmbedList])

  useEffect(() => {
    if (document && hasInstagramEmbed && !document.getElementById(instEmbedScript)) {
      const scriptElement = document.createElement('script')
      scriptElement.setAttribute('id', instEmbedScript)
      scriptElement.async = true
      scriptElement.setAttribute('src', embedJsScriptSrc)
      document.head.appendChild(scriptElement)
    }
  }, [hasInstagramEmbed])

  return (
    <Box width='100%' m='auto' ref={containerRef}>
      {smEmbedList.length &&
        smEmbedList?.map((embed, i) => (
          <Container
            key={`smEmbed-${embed.i}`}
            color={color}
            sx={{
              width: '100%',
              position: 'relative',
              padding: 0
            }}
            spacing={i === 0 && !isTopSpace ? { base: '0', md: '0' } : { base: '32px 0 0', md: '48px 0 0' }}
          >
            <Box
              className='disabled-container'
              sx={{
                display: 'none',
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 1,
                backgroundColor: 'transparent'
              }}
            />
            {renderSwitch(embed, hasInstagramEmbed)}
          </Container>
        ))}
    </Box>
  )
}
